import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Kanban,
  MoreHorizontal,
  DollarSign,
  Building,
  AlertTriangle,
  CheckCircle,
  ClipboardList,
  ShieldAlert
} from 'lucide-react';

const PipelinePage = () => {
  const navigate = useNavigate();
  
  const [stages, setStages] = useState([]);
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedDeal, setDraggedDeal] = useState(null);
  
  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);

  useEffect(() => {
    loadPipeline();
  }, []);

  const loadPipeline = async () => {
    try {
      const [stagesRes, boardRes] = await Promise.all([
        api.get('/pipeline/stages'),
        api.get('/pipeline/board')
      ]);
      
      setStages(stagesRes.data);
      setBoard(boardRes.data);
    } catch (error) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedDeal || draggedDeal.stage === targetStage) {
      setDraggedDeal(null);
      return;
    }

    // Validate the move first
    try {
      const validationRes = await api.post(
        `/deals/${draggedDeal.deal_id}/validate-stage-move?target_stage=${encodeURIComponent(targetStage)}`
      );
      
      if (!validationRes.data.can_move) {
        // Show validation modal
        setValidationData(validationRes.data);
        setPendingMove({ deal: draggedDeal, targetStage });
        setShowValidationModal(true);
        setDraggedDeal(null);
        return;
      }
      
      // Move is allowed, proceed
      await executeDealMove(draggedDeal.deal_id, targetStage, false);
    } catch (error) {
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object' && detail.incomplete_items) {
          setValidationData(detail);
          setPendingMove({ deal: draggedDeal, targetStage });
          setShowValidationModal(true);
        } else {
          toast.error(detail.message || 'Failed to move deal');
        }
      } else {
        toast.error('Failed to move deal');
      }
    }
    
    setDraggedDeal(null);
  };

  const executeDealMove = async (dealId, targetStage, adminOverride = false) => {
    try {
      await api.put(`/deals/${dealId}?admin_override=${adminOverride}`, { stage: targetStage });
      toast.success(`Deal moved to ${targetStage}`);
      loadPipeline();
    } catch (error) {
      toast.error('Failed to move deal');
    }
  };

  const handleAdminOverride = async () => {
    if (pendingMove) {
      await executeDealMove(pendingMove.deal.deal_id, pendingMove.targetStage, true);
      setShowValidationModal(false);
      setPendingMove(null);
      setValidationData(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedDeal(null);
  };

  const getStageColor = (stageName) => {
    const colors = {
      'New Lead': 'bg-blue-500',
      'Contacted': 'bg-purple-500',
      'Application Started': 'bg-amber-500',
      'Docs Received': 'bg-teal-500',
      'Underwriting': 'bg-indigo-500',
      'Approved': 'bg-green-500',
      'Closed': 'bg-emerald-700',
      'Lost': 'bg-red-500'
    };
    return colors[stageName] || 'bg-gray-500';
  };

  const getTotalValue = (deals) => {
    return deals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
  };

  const getChecklistIndicator = (deal) => {
    const summary = deal.checklist_summary;
    if (!summary || summary.total === 0) return null;
    
    const percentage = Math.round((summary.completed / summary.total) * 100);
    const isComplete = summary.required_completed === summary.required_total;
    
    return (
      <div className="flex items-center gap-2 mt-2" data-testid={`checklist-indicator-${deal.deal_id}`}>
        <div className="flex-1">
          <Progress value={percentage} className="h-1.5" />
        </div>
        <span className={`text-xs font-medium ${isComplete ? 'text-green-500' : 'text-muted-foreground'}`}>
          {summary.completed}/{summary.total}
        </span>
        {isComplete ? (
          <CheckCircle className="h-3 w-3 text-green-500" />
        ) : (
          <ClipboardList className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-48"></div>
            <div className="flex gap-4 overflow-hidden">
              {[1,2,3,4].map(i => (
                <div key={i} className="w-80 h-96 bg-muted rounded-xl flex-shrink-0"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="pipeline-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Kanban className="h-8 w-8" />
              Pipeline
            </h1>
            <p className="text-muted-foreground mt-1">
              Drag and drop deals between stages • Checklist validation enforced
            </p>
          </div>
        </div>

        {/* Kanban Board */}
        <ScrollArea className="w-full pb-4">
          <div className="flex gap-4 min-w-max">
            {stages.map((stage) => {
              const stageData = board[stage.name] || { deals: [] };
              const deals = stageData.deals || [];
              const totalValue = getTotalValue(deals);
              
              return (
                <div
                  key={stage.stage_id}
                  className="kanban-column flex flex-col"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.name)}
                  data-testid={`stage-${stage.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getStageColor(stage.name)}`}></div>
                      <h3 className="font-semibold">{stage.name}</h3>
                      <Badge variant="secondary" className="ml-1">
                        {deals.length}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Total Value */}
                  {totalValue > 0 && (
                    <div className="mb-3 text-sm text-muted-foreground">
                      Total: ${totalValue.toLocaleString()}
                    </div>
                  )}
                  
                  {/* Deal Cards */}
                  <div className="flex-1 space-y-3">
                    {deals.length > 0 ? (
                      deals.map((deal) => (
                        <div
                          key={deal.deal_id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, deal)}
                          onDragEnd={handleDragEnd}
                          className={`kanban-card ${draggedDeal?.deal_id === deal.deal_id ? 'opacity-50' : ''}`}
                          data-testid={`deal-card-${deal.deal_id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant="outline">{deal.loan_type}</Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/contacts/${deal.contact_id}`)}>
                                  View Contact
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          
                          {/* Contact Info */}
                          {deal.contact && (
                            <div 
                              className="flex items-center gap-2 mb-3 cursor-pointer hover:opacity-80"
                              onClick={() => navigate(`/contacts/${deal.contact_id}`)}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                  {deal.contact.first_name?.[0]}{deal.contact.last_name?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">
                                  {deal.contact.first_name} {deal.contact.last_name}
                                </p>
                                {deal.contact.company && (
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Building className="h-3 w-3" />
                                    {deal.contact.company}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Deal Amount */}
                          {deal.amount && (
                            <div className="flex items-center gap-1 text-lg font-semibold text-primary">
                              <DollarSign className="h-4 w-4" />
                              {deal.amount.toLocaleString()}
                            </div>
                          )}
                          
                          {/* Property Type */}
                          {deal.property_type && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {deal.property_type}
                            </p>
                          )}
                          
                          {/* Checklist Indicator */}
                          {getChecklistIndicator(deal)}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p className="text-sm">No deals</p>
                        <p className="text-xs mt-1">Drag deals here</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Validation Modal */}
        <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
          <DialogContent data-testid="validation-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-500">
                <AlertTriangle className="h-5 w-5" />
                Checklist Incomplete
              </DialogTitle>
              <DialogDescription>
                {validationData?.message || 'Complete required checklist items before moving to the next stage.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm font-medium mb-3">Incomplete required items:</p>
              <ul className="space-y-2">
                {validationData?.incomplete_items?.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="h-2 w-2 rounded-full bg-amber-500"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowValidationModal(false);
                  setPendingMove(null);
                  setValidationData(null);
                }}
              >
                Cancel
              </Button>
              {pendingMove && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/contacts/${pendingMove.deal.contact_id}`)}
                >
                  View Deal
                </Button>
              )}
              {validationData?.can_override && (
                <Button 
                  variant="destructive" 
                  onClick={handleAdminOverride}
                  data-testid="admin-override-btn"
                >
                  <ShieldAlert className="mr-2 h-4 w-4" />
                  Admin Override
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default PipelinePage;
